<template>
  <div class="vendor-message-detail-page-shell">
    <p v-if="errorMessage" class="result-error">{{ errorMessage }}</p>
    <div ref="chatThreadRef" class="chat-thread">
      <div v-if="isLoading" class="fetching-state">
        <LoadingState
          :loading="isLoading"
          message="Loading conversation..."
          :spinner-size="24"
          :spinner-width="3"
        />
      </div>
      <div v-else class="thread-content">
        <div
          v-for="item in messages"
          :key="item.id"
          class="message-row"
          :class="item.role"
        >
          <div v-if="item.role === 'incoming'" class="avatar">
            {{ item.initials }}
          </div>
          <div class="bubble" :class="item.role">
            <div
              class="bubble-text"
              :class="item.message_type"
              @click="handleBubbleClick(item)"
              v-html="item.text"
            ></div>
            <div class="bubble-meta">
              <span>{{ timeAgo(item.time) }}</span>
              <v-icon v-if="item.role === 'outgoing'" size="12">mdi-check-all</v-icon>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="composer">
      <div class="composer-field">
        <AppTextArea
          hide-details
          density="comfortable"
          rows="2"
          v-model="messageInput"
          placeholder="Type a message..."
        />
      </div>
      <v-btn
        icon
        color="primary"
        :loading="isSending"
        :disabled="!messageInput.trim() || isSending"
        @click="handleSend"
      >
        <v-icon size="18">mdi-send</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import AppTextArea from "@/shared/components/ui/AppTextArea.vue";
import LoadingState from "@/shared/components/feedback/LoadingState.vue";
import {
  fetchVendorMessageDetail,
  sendVendorMessage,
} from "@/shared/services/messages.api";
import { useMessageHeaderStore } from "@/shared/stores/messageHeader";
import { presetConversations } from "./presetConversations";
import { timeAgo } from "@/shared/utils/utils";
import { getCurrentUserRole } from "@/shared/utils/currentUserRole";
import { getActiveContext } from "@/modules/auth/services/auth.api";
const router = useRouter();
type MessageItem = {
  id: string;
  role: "incoming" | "outgoing";
  initials: string;
  message_type: string;
  metadata?: {
    table_id?: string;
    table_name?: string;
  };
  text: string;
  time: string;
};

type MessageDetailRecord = {
  chat_thread_id: number;
  chat_thread_type: string;
  title: string;
  status: string;
  client: {
    id: number;
    name: string;
    avatar: string;
    initials: string;
  };
  messages: Array<{
    id: number;
    body: string;
    message_type: string;
    metadata: {
      table_id?: string;
      table_name?: string;
    };
    is_system_message: boolean;
    created_at: string;
    sender: {
      role: string | null;
      name: string;
      avatar: string | null;
    };
  }>;
};

type MessageDetailResponse = MessageDetailRecord | { data: MessageDetailRecord };

const route = useRoute();
const messageHeader = useMessageHeaderStore();
const messageInput = ref("");
const messages = ref<MessageItem[]>(presetConversations);
const isLoading = ref(false);
const isSending = ref(false);
const errorMessage = ref("");
const chatThreadRef = ref<HTMLElement | null>(null);
const currentThreadId = computed(() => asString(route.params.id).trim());
let activeVendorThreadId = "";
let activeVendorChannel: any = null;

function asString(value: unknown, fallback = "") {
  if (typeof value === "string" || typeof value === "number") return String(value);
  return fallback;
}

function getAppointmentDetailRouteName() {
  const storedRole = String(getCurrentUserRole() || "").trim().toLowerCase();
  const activeContext = (getActiveContext() as any) || {};
  const contextRoles = Array.isArray(activeContext.roles)
    ? activeContext.roles.map((role: unknown) => String(role || "").trim().toLowerCase()).filter(Boolean)
    : [];
  const role =
    storedRole ||
    contextRoles.find((item) => item === "service_provider" || item === "provider") ||
    contextRoles[0] ||
    String(activeContext.type || "").trim().toLowerCase();
  if (role === "service_provider" || role === "provider" || role.includes("provider")) {
    return "provider.appointment.detail";
  }
  return "vendor.appointment.detail";
}

function getInitials(value: string) {
  const normalized = value.trim().replace(/\s+/g, " ");
  if (!normalized) return "NA";

  const parts = normalized.split(" ");
  const initials =
    parts.length >= 2
      ? `${parts[0].charAt(0)}${parts[1].charAt(0)}`
      : parts[0].slice(0, 2);

  return initials.toUpperCase().padEnd(2, "X").slice(0, 2);
}

function toHeaderVendor(client: MessageDetailRecord["client"]) {
  return {
    id: client.id,
    name: client.name,
    logo: client.avatar,
    initials: client.initials || getInitials(client.name),
  };
}

function normalizeMessage(item: MessageDetailRecord["messages"][number]): MessageItem {
  const senderName = item.sender.name;
  return {
    id: String(item.id),
    role: item.sender.role === "vendor" ? "outgoing" : "incoming",
    initials: getInitials(senderName),
    message_type: item.message_type,
    metadata: item.metadata,
    text: item.body,
    time: item.created_at,
  };
}

function normalizeReverbMessage(event: any): MessageItem | null {
  const payload = event?.message ?? event;
  if (
    !payload ||
    payload.id == null ||
    !payload.body ||
    !payload.created_at ||
    !payload.sender
  ) {
    return null;
  }

  return {
    id: String(payload.id),
    role: payload.sender.role === "vendor" ? "outgoing" : "incoming",
    initials: getInitials(payload.sender.name || "NA"),
    message_type: payload.message_type || "text",
    text: payload.body,
    time: payload.created_at,
  };
}

function appendReverbMessage(event: any) {
  const normalized = normalizeReverbMessage(event);
  if (!normalized) return;

  if (
    messages.value.some(
      (item) =>
        item.id === normalized.id ||
        (item.role === normalized.role &&
          item.text === normalized.text &&
          item.time === normalized.time)
    )
  ) {
    return;
  }

  messages.value = [...messages.value, normalized];
}

function getMessageThread(response: MessageDetailResponse) {
  if ("messages" in response && Array.isArray(response.messages)) {
    return response.messages;
  }

  if ("data" in response && response.data && Array.isArray(response.data.messages)) {
    return response.data.messages;
  }

  return [];
}

function listenToReverb(threadId: string) {
  if (!threadId) return;

  if (!window.Echo) {
    return;
  }

  if (activeVendorThreadId === threadId) {
    return;
  }

  if (activeVendorThreadId) {
    stopListeningToReverb(activeVendorThreadId);
  }

  console.log("Listening to Reverb channel:", `chat.thread.${threadId}`);

  activeVendorChannel = window.Echo.private(`chat.thread.${threadId}`);
  activeVendorChannel.listen(".message.sent", (event: any) => {
    console.log("Reverb message received:", event);
    appendReverbMessage(event);
  });
  activeVendorThreadId = threadId;
}

function stopListeningToReverb(threadId: string) {
  if (!threadId || !window.Echo) return;
  if (activeVendorThreadId !== threadId) return;

  console.log("Leaving Reverb channel:", `private-chat.thread.${threadId}`);

  if (activeVendorChannel?.stopListening) {
    activeVendorChannel.stopListening(".message.sent");
  }

  window.Echo.leave(`private-chat.thread.${threadId}`);
  activeVendorChannel = null;
  activeVendorThreadId = "";
}

function handleAuthChanged() {
  if (currentThreadId.value) {
    listenToReverb(currentThreadId.value);
  }
}

async function loadConversation() {
  const id = asString(route.params.id).trim();
  if (!id) {
    errorMessage.value = "Conversation not found.";
    messageHeader.reset();
    return;
  }

  isLoading.value = true;
  errorMessage.value = "";
  messageHeader.setHeader({
    isLoading: true,
    title: "",
    chatThreadType: "",
    vendor: null,
  });

  try {
    const response = (await fetchVendorMessageDetail(id)) as MessageDetailResponse;
    if ("data" in response && response.data) {
      messageHeader.setHeader({
        title: response.data.title,
        chatThreadType: response.data.chat_thread_type,
        vendor: toHeaderVendor(response.data.client),
        isLoading: false,
      });
    } else if ("title" in response) {
      messageHeader.setHeader({
        title: response.title,
        chatThreadType: response.chat_thread_type,
        vendor: toHeaderVendor(response.client),
        isLoading: false,
      });
    } else {
      messageHeader.setHeader({ isLoading: false });
    }
    messages.value = getMessageThread(response).map(normalizeMessage);
  } catch (error) {
    console.error(error);
    errorMessage.value = "Unable to load conversation.";
    messageHeader.setHeader({ isLoading: false });
  } finally {
    isLoading.value = false;
  }
}

function scrollToBottom() {
  nextTick(() => {
    requestAnimationFrame(() => {
      const el = chatThreadRef.value;
      if (!el) return;

      if (typeof el.scrollTo === "function") {
        el.scrollTo({ top: el.scrollHeight, behavior: "auto" });
        return;
      }

      el.scrollTop = el.scrollHeight;
    });
  });
}

async function handleSend() {
  if (isSending.value) return;
  const text = messageInput.value.trim();
  if (!text) return;
  const id = asString(route.params.id).trim();
  if (!id) return;

  isSending.value = true;
  try {
    const response = await sendVendorMessage(id, text);
    const thread = getMessageThread(response as MessageDetailResponse);
    if (thread.length > 0) {
      messages.value = thread.map(normalizeMessage);
    } else {
      const outgoing: MessageItem = {
        id: `local-${Date.now()}`,
        role: "outgoing",
        initials: "",
        text,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        message_type: "text",
      };
      // messages.value = [...messages.value, outgoing];
    }
    messageInput.value = "";
  } catch (error) {
    console.error("Failed to send message", error);
    errorMessage.value = "Failed to send message.";
  } finally {
    isSending.value = false;
  }
}

function handleBubbleClick(item: MessageItem) {
  if (item.metadata?.table_id) {
    console.log("Bubble clicked with table_id:", item.metadata);
    switch (item.metadata.table_name) {
      case "quote_enqs":
        router.push({
          name: "vendor.inquiry.detail",
          params: { id: item.metadata.table_id },
        });

        break;

      case "appointments":
        router.push({
          name: getAppointmentDetailRouteName(),
          params: { id: item.metadata.table_id },
        });

        break;
      case "quotes":
        router.push({
          name: "vendor.quotations.detail",
          params: { id: item.metadata.table_id },
        });

        break;
      default:
        console.log("Unknown table name");
    }
  }
}

watch(
  () => currentThreadId.value,
  async (newThreadId, oldThreadId) => {
    if (oldThreadId) {
      stopListeningToReverb(oldThreadId);
    }

    await loadConversation();

    if (newThreadId) {
      listenToReverb(newThreadId);
    }
  },
  { immediate: true }
);

watch(
  messages,
  () => {
    scrollToBottom();
  },
  { flush: "post" }
);

watch(
  () => isLoading.value,
  (loading, previousLoading) => {
    if (previousLoading && !loading) {
      scrollToBottom();
    }
  },
  { flush: "post" }
);

onMounted(() => {
  window.addEventListener("auth-changed", handleAuthChanged);
});

onUnmounted(() => {
  window.removeEventListener("auth-changed", handleAuthChanged);
  if (currentThreadId.value) {
    stopListeningToReverb(currentThreadId.value);
  }

  messageHeader.reset();
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (activeVendorThreadId) {
      stopListeningToReverb(activeVendorThreadId);
    }
  });
}
</script>

<style lang="scss">
.vendor-message-detail-page-shell {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 160px);
  padding: 0px 16px 0 16px !important;
  background: #f8fafc;
  overflow: hidden;
  .fetching-state {
    padding: 12px 0;
  }

  .result-error {
    margin: 0;
    font-size: 14px;
    color: #b91c1c;
    font-weight: 600;
  }

  .chat-thread {
    display: flex;
    flex-direction: column;
    gap: 14px;
    flex: 1;
    padding: 20px 0px;
    overflow-y: auto;
    min-height: 0;
  }

  .thread-content {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .message-row {
    display: flex;
    align-items: flex-end;
    gap: 10px;
  }

  .message-row.outgoing {
    justify-content: flex-end;
  }

  .avatar {
    width: 30px;
    height: 30px;
    border-radius: 999px;
    background: #fde2e4;
    color: #9f1239;
    display: grid;
    place-items: center;
    font-size: var(--font-size-default);
    font-weight: 700;
    flex-shrink: 0;
  }

  .bubble {
    max-width: 78%;
    padding: 12px 14px;
    border-radius: 16px;
    background: #ffffff;
    border: 1px solid #26262612;
  }

  .bubble.outgoing {
    background: #ffd2c4;
    color: #360f01;
    border-bottom-right-radius: 6px;
    border: 1px solid #26262612;
  }

  .bubble.incoming {
    border-bottom-left-radius: 6px;
  }

  .bubble-text {
    margin: 0;
    font-size: 15px;
    line-height: 1.4;
  }

  .bubble-meta {
    margin-top: 8px;
    font-size: var(--font-size-sm);
    display: flex;
    align-items: center;
    gap: 6px;
    color: #636262;
  }

  .bubble.incoming .bubble-meta {
    color: #636262;
  }

  .composer {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px 18px;
    background: #f8fafc;
    border-top: 1px solid #eef0f3;
    z-index: 2;
  }

  .composer-field {
    flex: 1;
  }

  .composer-field :deep(.v-field) {
    border-radius: 14px;
  }
  .has-link {
    color: #0069ff !important;
    text-decoration: underline;
  }
  .reason-text {
    color: #dc2626;
  }
  .reason-block {
    margin-top: 10px !important;
  }
}
</style>
